import React, {Dispatch, FC, useRef, useState} from 'react';
import {useNavigate, useOutletContext, useParams} from "react-router-dom";
import {
    checkoutCartItemWithImg, createOrderFormData, createOrderFormRoomData,
    getCartTotal,
    getMaterialStrings, textToLink,
    useAppSelector
} from "../../helpers/helpers";
import {CheckoutSchema} from "./CheckoutSchema";
import {pdf} from "@react-pdf/renderer";
import PDFOrder from "../PDFOrder/PDFOrder";
import {saveAs} from "file-saver";
import {Form, Formik} from "formik";
import s from "./checkout.module.sass";
import {AdditionalEmailsArray, FileInput, MyDatePicker, PhoneInput, TextInput} from "../../common/Form";
import CheckoutCart from "./CheckoutCart";
import {MaybeNull, MaybeUndefined} from "../../helpers/productTypes";
import {RoomFront} from "../../helpers/roomTypes";
import {RoomsState} from "../../store/reducers/roomSlice";
import {getPurchaseRoomsOrder, sendOrder} from "../../api/apiFunctions";
import {PurchaseOrdersState} from "../../store/reducers/purchaseOrderSlice";
import PDFPurchaseOrder from "../PDFOrder/PDFPurchaseOrder";
import CheckoutButtonRow from "./CheckoutButtonRow";
import {useAuthUser} from "../../utils/customHooks";
import {useEditor} from "../../helpers/EditorContext";

export type ButtonType = 'save-room' | 'send-room' | 'save-po' | 'send-po';

export type CheckoutFormType = {
    name: string,
    company: string,
    purchase_order: string,
    room_name: string,
    email: string,
    phone: string,
    delivery: string,
    delivery_date: MaybeNull<Date>,
    additional_emails: string[],
    files: MaybeUndefined<File[]>
    manager_email: MaybeNull<string>,
}

export const MAX_FILES = 5;
export const MAX_FILE_SIZE_MB = 5;

const CheckoutForm: FC = () => {
    const {purchase_order_name} = useParams()
    const navigate = useNavigate();
    const clickedButtonRef = useRef<MaybeNull<ButtonType>>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const {purchase_orders} = useAppSelector<PurchaseOrdersState>(state => state.purchase_order);
    const {room} = useOutletContext<{room: RoomFront}>();
    const user = useAuthUser();
    const is_my_project = useEditor() === 'designer';
    const editable_user = useAppSelector(state => state.admin.editable_user);
    const {cart_items} = useAppSelector<RoomsState>(state => state.room)!
    const active_po = purchase_orders.find(po => textToLink(po.name) === purchase_order_name && !po.is_archived)?.name

    const {
        _id,
        purchase_order_id,
        activeProductCategory,
        ...materials
    } = room;
    const total = getCartTotal(cart_items);
    const materialStrings = getMaterialStrings(materials);

    const handleSubmit = async (values: CheckoutFormType) => {
        if (!values.delivery_date) return;
        const validatedValues = {
            ...values,
            delivery_date: values.delivery_date,
            additional_emails: values.additional_emails ? [...new Set(values.additional_emails.filter(str => str.trim() !== ""))] : []
        };

        const button_type = clickedButtonRef.current;
        if (!button_type || !cart_items) return;
        const date = new Date().toLocaleString('ru-RU', {dateStyle: "short"});
        const fileName = `${textToLink(validatedValues.purchase_order)}.${date}`;

        switch (button_type) {
            case "save-room": {
                const cartWithJPG = checkoutCartItemWithImg(cart_items, room);
                const room_blob = await pdf(<PDFOrder values={validatedValues}
                                                      materialStrings={materialStrings}
                                                      cart={cartWithJPG}/>).toBlob();
                saveAs(room_blob, `${fileName}.pdf`);
                break;
            }
            case "save-po": {
                const po_rooms_api = await getPurchaseRoomsOrder(purchase_order_id);
                if (!po_rooms_api) return;
                const po_blob = await pdf(<PDFPurchaseOrder values={validatedValues}
                                                            po_rooms_api={po_rooms_api}/>).toBlob();
                saveAs(po_blob, `${fileName}.pdf`);
                break;
            }
            case "send-room": {
                const cartWithJPG = checkoutCartItemWithImg(cart_items, room);
                const room_blob = await pdf(<PDFOrder values={validatedValues}
                                                      materialStrings={materialStrings}
                                                      cart={cartWithJPG}/>).toBlob();
                const RoomFormData = await createOrderFormRoomData(room, cart_items, room_blob, validatedValues, fileName, date);
                const res = await sendOrder(RoomFormData, textToLink(company));
                if (res?.status === 201) setIsModalOpen(true);
                break;

            }
            case "send-po": {
                const po_rooms_api = await getPurchaseRoomsOrder(purchase_order_id);
                if (!po_rooms_api) return;
                const po_blob = await pdf(<PDFPurchaseOrder values={validatedValues}
                                                            po_rooms_api={po_rooms_api}/>).toBlob();
                const POFormData = await createOrderFormData(po_rooms_api, po_blob, validatedValues, fileName, date);
                const res = await sendOrder(POFormData, textToLink(company));
                if (res?.status === 201) setIsModalOpen(true);
                break;
            }
        }
    }

    const has_editable_user = !is_my_project && !!editable_user
    if (!cart_items?.length || !active_po) {
        navigate(-1);
        return null;
    }
    const {name, company, email, additional_emails, phone} = user;

    const initialValues: CheckoutFormType = !has_editable_user ? {
        name,
        company,
        email,
        additional_emails,
        phone,
        purchase_order: active_po,
        room_name: room.name,
        delivery: '',
        delivery_date: null,
        files: [],
        manager_email: null
    } : {
        name: editable_user.name,
        company: editable_user.company,
        email: editable_user.email,
        additional_emails: editable_user.additional_emails,
        phone: editable_user.phone,
        purchase_order: active_po,
        room_name: room.name,
        delivery: '',
        delivery_date: null,
        files: [],
        manager_email: email
    };
    const schema = CheckoutSchema(MAX_FILES, MAX_FILE_SIZE_MB)
    return (
        <Formik initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSubmit}
        >
            {({values, errors}) => {
                console.log(errors)
                return (
                    <Form className={[s.form].join(' ')}>
                        <h1>Checkout</h1>
                        <div className={s.block}>
                            {isModalOpen ? <EmailWasSent setIsModalOpen={setIsModalOpen}/> : null}
                            <TextInput type="text" name="name" label="Name"/>
                            <TextInput type="text" name="company" label="Company"/>
                            <TextInput type="text" name="purchase_order" label="PO name"/>
                            <TextInput type="text" name="room_name" label="Room name"/>
                            <TextInput type="email" name="email" label="E-mail"/>
                            <AdditionalEmailsArray additional_emails={values.additional_emails}
                                                   errors={errors.additional_emails}/>
                            <PhoneInput type="text" name="phone" label="Phone number"/>
                            <TextInput type="text" name="delivery" label="Delivery address"/>
                            <MyDatePicker name="delivery_date" weeks={4} label="Delivery date"/>
                            <FileInput name="files"
                                       label="Attachments"
                                       multiple={true}
                                       accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                       max_files={MAX_FILES}
                                       max_mb={MAX_FILE_SIZE_MB}
                            />
                        </div>
                        <CheckoutCart cart={cart_items} total={total}/>
                        <CheckoutButtonRow clickedButtonRef={clickedButtonRef} handleSubmit={handleSubmit}
                                           purchase_order_id={purchase_order_id}/>
                    </Form>
                )
            }}
        </Formik>
    );
};

export default CheckoutForm;

const EmailWasSent: FC<{ setIsModalOpen: Dispatch<boolean> }> = ({setIsModalOpen}) => {
    const navigate = useNavigate();
    setTimeout(() => {
        setIsModalOpen(false)
        navigate(`/profile/purchase`)
    }, 4000)
    return (
        <div className={s.notificationWrap}>
            <div className={s.notification}>Email sent. Thank you!</div>
        </div>
    )
}